import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { FormReportFlagIssuesData } from 'dashboard/forms/FormReportFlagIssues';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Report, ReportStatus } from 'dashboard/models/report';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearReport } from 'dashboard/redux/actions/report';
import { RootState } from 'dashboard/redux/store';
import {
  loadReport,
  pauseReport,
  printReport,
  redactReport,
  resumeReport,
  runReportAdhoc,
} from 'dashboard/redux/thunk-actions/report';
import DashboardReportShowView from 'dashboard/views/DashboardReportsShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useInterval from 'lib/hooks/useInterval';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { captureException } from 'lib/reporting';

type Params = { reportId: string };

const DashboardReportsShowRoute = (props: PropsFromRedux) => {
  const [isPollRunning, setIsPollRunning] = useState(true);
  const params = useParams<Params>() as Params;

  useOnMountAndUnmount(props.clearReport);

  useRedirectOnEnvironmentChange('/dashboard/reports');

  const { loading, callback: loadReport } = useApiLoader({
    loader: () => props.loadReport(params.reportId, props.currentEnvironment),
    deps: [props.loadReport, params.reportId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load report.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: pollReport, error: pollReportError } = useApiCallback(async () => {
    if (props.report?.attributes?.status === ReportStatus.Pending) {
      props.loadReport(params.reportId, props.currentEnvironment);
    } else {
      setIsPollRunning(false);
    }
  }, [props.report, props.loadReport, params.reportId]);

  useApiErrorHandler({
    error: pollReportError,

    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to refresh report.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues.', MessageType.Failure),
  });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  useInterval({ callback: pollReport, delay: isPollRunning ? 5000 : null });

  const { error: printReportError, callback: handlePrintReport } = useApiCallback(async () => {
    props.showMessage('Exporting Report as PDF, please wait...', MessageType.Notice),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.printReport(props.report.id);
  }, [props.report, props.printReport]);

  useApiErrorHandler({
    error: printReportError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to download as PDF, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to download as PDF, please try again.', MessageType.Failure),
  });

  const { callback: handleUpdateTags, error: handleUpdateTagsError } = useApiCallback(
    async ({ tags: tagIds }: FormUpdateTagsData) => {
      await SessionClientDashboard.postResourceAction<Report>(
        {
          type: ApiModel.Report,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.report.id,
        },
        'set-tags',
        { meta: { 'tag-id': tagIds } },
      );

      await loadReport();
    },
    [props.report, props.reportTags, loadReport],
  );

  useApiErrorHandler({
    error: handleUpdateTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to add tags to report.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to add tags to report.', MessageType.Failure),
  });

  const { error: flagIssuesError, callback: handleFlagIssues } = useApiCallback(
    async ({ description }: FormReportFlagIssuesData) => {
      await SessionClientDashboard.postResourceAction<Report>(
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.report.id,
          type: ApiModel.Report,
        },
        'flag-issues',
        {
          meta: {
            description,
          },
        },
      );
      props.showMessage(
        'Thank you for reporting this issue! We review and track these issues to inform product improvements.',
        MessageType.Success,
      );
    },
    [props.report, props.showMessage],
  );

  useApiErrorHandler({
    error: flagIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to flag issue to report, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to flag issue to report, please try again.', MessageType.Failure),
  });

  const { error: runReportAdhocError, callback: handleRunReportAdhoc } =
    useApiCallback(async () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      props.runReportAdhoc(props.report.id);
      props.showMessage('Re-running this report.', MessageType.Success);
    }, [props.report, props.showMessage]);

  useApiErrorHandler({
    error: runReportAdhocError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to re-run report, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to re-run report, please try again.', MessageType.Failure),
  });

  const { error: pauseReportError, callback: handlePauseReport } = useApiCallback(async () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await props.pauseReport(props.report.id);
    props.showMessage('Successfully paused this report.', MessageType.Success);
    await loadReport();
  }, [props.report, props.showMessage]);

  useApiErrorHandler({
    error: pauseReportError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to pause report, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to pause report, please try again.', MessageType.Failure),
  });

  const { error: resumeReportError, callback: handleResumeReport } = useApiCallback(async () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    props.resumeReport(props.report.id);
    props.showMessage('Successfully resumed this report.', MessageType.Success);
    await loadReport();
  }, [props.report, props.showMessage]);

  useApiErrorHandler({
    error: resumeReportError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to resume report, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to resume report, please try again.', MessageType.Failure),
  });

  const { error: redactReportError, callback: handleRedactReport } = useApiCallback(async () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await props.redactReport(props.report.id);
    await loadReport();
    props.showMessage('Successfully redacted report.', MessageType.Success);
  }, [props.report, props.redactReport]);

  useApiErrorHandler({
    error: redactReportError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to redact report, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to redact report, please try again.', MessageType.Failure),
  });

  return (
    <DashboardReportShowView
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      report={props.report}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportTemplate={props.reportTemplate}
      reportTags={props.reportTags}
      cases={props.cases}
      users={props.users}
      relatedReports={props.relatedReports}
      featureFlags={props.featureFlags}
      isLoading={loading}
      handlePrintReport={handlePrintReport}
      handleUpdateTags={handleUpdateTags}
      handleFlagIssues={handleFlagIssues}
      handleRunReportAdhoc={handleRunReportAdhoc}
      handlePauseReport={handlePauseReport}
      handleResumeReport={handleResumeReport}
      handleRedactReport={handleRedactReport}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    featureFlags: state.featureFlags.featureFlags,
    report: state.report.report,
    reportTemplate: state.report.reportTemplate,
    reportTags: state.report.tags,
    cases: state.report.cases,
    users: state.user.users,
    relatedReports: state.report.relatedReports,
  };
};

const mapDispatchToProps = {
  clearReport,
  loadReport,
  printReport,
  showMessage,
  pauseReport,
  redactReport,
  resumeReport,
  runReportAdhoc,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardReportsShowRoute);
